/**
 * SearchModal — Cmd/Ctrl+K spotlight search for conversations.
 *
 * Features:
 *  - Searches session titles and message content via useSessions.searchSessions
 *  - Keyboard navigation (Arrow Up/Down + Enter)
 *  - Escape to close
 *  - Renders as a modal overlay
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, MessageSquare, X } from 'lucide-react'
import { useSessions } from '../../hooks/useSessions'

export default function SearchModal({ isOpen, onClose, onSelectSession }) {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const { searchSessions, sessions } = useSessions()
  const inputRef = useRef(null)

  // Get search results
  const results = query.trim()
    ? searchSessions(query).slice(0, 10)
    : sessions.slice(0, 8) // Show recent when no query

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setActiveIndex(0)
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [isOpen])

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(prev => Math.min(prev + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault()
      onSelectSession(results[activeIndex].id)
      onClose()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }, [results, activeIndex, onSelectSession, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-dark-raised rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search conversations..."
            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
          />
          <kbd className="hidden sm:inline text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded font-mono">
            ESC
          </kbd>
        </div>

        {/* Results list */}
        <div className="max-h-72 overflow-y-auto">
          {results.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
              {query.trim() ? 'No conversations found' : 'No conversations yet'}
            </div>
          ) : (
            <ul className="py-1">
              {results.map((session, idx) => (
                <li key={session.id}>
                  <button
                    onClick={() => {
                      onSelectSession(session.id)
                      onClose()
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      idx === activeIndex
                        ? 'bg-amtrend-blue/10 text-gray-900 dark:text-gray-100'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay'
                    }`}
                  >
                    <MessageSquare
                      size={16}
                      className={idx === activeIndex ? 'text-amtrend-blue' : 'text-gray-400'}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {session.title || 'New conversation'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {session.messages?.length || 0} messages
                        {session.updatedAt && (
                          <> &middot; {formatRelativeTime(session.updatedAt)}</>
                        )}
                      </p>
                    </div>
                    {idx === activeIndex && (
                      <kbd className="hidden sm:inline text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-surface-dark-overlay px-1.5 py-0.5 rounded font-mono">
                        &#9166;
                      </kbd>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 flex items-center gap-4 text-[10px] text-gray-400 dark:text-gray-500">
          <span>&#8593;&#8595; navigate</span>
          <span>&#9166; select</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  )
}

// Format a date to relative time (e.g., "2h ago", "Yesterday")
function formatRelativeTime(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
